import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Plus, Search, MapPin, ChevronRight, Download } from "lucide-react";
import api from "../lib/api";
import { statusClass, exportCSV } from "../lib/format";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TYPES = ["Injector Clinic", "Medical Spa", "Dermatology", "Plastic Surgery", "GP Clinic", "ENT Clinic", "Hospital"];
const STATUSES = ["Active", "Inactive", "Prospect", "Lost"];

export default function Clinics() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.get("/clinics").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Clinic name is required"); return; }
    try { await api.post("/clinics", form); toast.success("Clinic added"); setOpen(false); setForm({}); load(); }
    catch { toast.error("Save failed"); }
  };

  const filtered = rows.filter((r) =>
    (statusFilter === "All" || r.status === statusFilter) &&
    (!q || [r.name, r.city, r.region, r.assigned_bdm_name].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase())))
  );

  return (
    <div>
      <PageHeader title="Clinic Management" subtitle="Your clinic database across NZ & Australia" icon={Building2}>
        <Button variant="outline" size="sm" onClick={() => exportCSV(filtered, "clinics.csv")} data-testid="export-clinics">
          <Download className="h-4 w-4 mr-1.5" /> Export
        </Button>
        <Button size="sm" onClick={() => { setForm({ status: "Prospect", country: "NZ" }); setOpen(true); }} className="bg-navy-900 hover:bg-navy-800" data-testid="add-clinic-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Clinic
        </Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clinics, cities, BDMs…" className="pl-9 bg-white" data-testid="clinic-search" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {["All", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s.toLowerCase()}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} onClick={() => navigate(`/clinics/${c.id}`)} data-testid={`clinic-card-${c.id}`}
            className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-slate-300 cursor-pointer transition-all group">
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl bg-brand-light flex items-center justify-center">
                <Building2 className="h-5 w-5 text-brand" />
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${statusClass(c.status)}`}>{c.status}</span>
            </div>
            <h3 className="font-display font-semibold text-navy-900 mt-3 group-hover:text-brand transition-colors">{c.name}</h3>
            <p className="text-xs text-slate-400 mt-0.5">{c.clinic_type}</p>
            <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-3">
              <MapPin className="h-3.5 w-3.5" /> {c.city}, {c.region} · {c.country}
            </div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-50">
              <span className="text-xs text-slate-500">BDM: <span className="font-medium text-navy-900">{c.assigned_bdm_name || "—"}</span></span>
              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-brand group-hover:translate-x-0.5 transition-all" />
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-slate-400 col-span-full text-center py-12">No clinics found</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Clinic</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <F label="Clinic Name *" full><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="clinic-name" /></F>
            <F label="Trading Name"><Input value={form.trading_name || ""} onChange={(e) => setForm({ ...form, trading_name: e.target.value })} /></F>
            <F label="Clinic Type">
              <Select value={form.clinic_type || ""} onValueChange={(v) => setForm({ ...form, clinic_type: v })}>
                <SelectTrigger data-testid="clinic-type"><SelectValue placeholder="Type" /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Address" full><Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
            <F label="Suburb"><Input value={form.suburb || ""} onChange={(e) => setForm({ ...form, suburb: e.target.value })} /></F>
            <F label="City"><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></F>
            <F label="Region"><Input value={form.region || ""} onChange={(e) => setForm({ ...form, region: e.target.value })} /></F>
            <F label="Country">
              <Select value={form.country || ""} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent><SelectItem value="NZ">New Zealand</SelectItem><SelectItem value="AU">Australia</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Postal Code"><Input value={form.postal_code || ""} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></F>
            <F label="Phone"><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></F>
            <F label="Email"><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
            <F label="Website"><Input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} /></F>
            <F label="Assigned BDM"><Input value={form.assigned_bdm_name || ""} onChange={(e) => setForm({ ...form, assigned_bdm_name: e.target.value })} /></F>
            <F label="Status">
              <Select value={form.status || ""} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Notes" full><Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-navy-900 hover:bg-navy-800" data-testid="save-clinic-btn">Create Clinic</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, full, children }) {
  return <div className={full ? "sm:col-span-2" : ""}><label className="text-xs font-semibold text-slate-600 mb-1.5 block">{label}</label>{children}</div>;
}
